import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ApiClientConfig} from '../../api-client-config';
import {map} from 'rxjs/operators';
import {BusinessProjectManagementService} from '../../interface/business-project-management-service';
import {ColumnWithLocalization} from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';

import {
  CommonDocumentEditorDetails,
  CovidAssessmentDetails,
  CovidAssessmentDocCreateRequest,
  CovidAssessmentDocUpdateRequest,
  CovidAssessmentOverrideOnDuplicate,
  DocRenameRequest,
  DocumentDuplicateResponse,
  DocumentLockResponse
} from '@primatoo-ms-organization';
import {
  GetCovidAsPermissionResponse
} from "it/primatoo/openapi/api/org/schemas/covidassessmentdoc/GetCovidAsPermissionResponse";
import {
  UpdateCovidAsPermissionRequest
} from "it/primatoo/openapi/api/org/schemas/covidassessmentdoc/UpdateCovidAsPermissionRequest";
import {
  GetCovidAsAvailableOperationsResponse
} from "it/primatoo/openapi/api/org/schemas/covidassessmentdoc/GetCovidAsAvailableOperationsResponse";
import {GetChapterResponse} from "it/primatoo/openapi/api/org/schemas/common/docs/GetChapterResponse";
import {ListTemplateChapterResponse} from "it/primatoo/openapi/api/org/schemas/common/docs/ListTemplateChapterResponse";
import {DocResourceStatus} from "it/primatoo/openapi/api/org/schemas/common/DocResourceStatus";
import {ListDocumentResponse} from "it/primatoo/openapi/api/org/schemas/common/docs/ListDocumentResponse";
import {CreateDocResponse} from 'it/primatoo/openapi/api/org/schemas/common/docs/CreateDocResponse';
import {DocumentItem} from "it/primatoo/openapi/api/org/schemas/common/docs/_listdocumentresponse/DocumentItem";
import {UpdateDocResponse} from "it/primatoo/openapi/api/org/schemas/common/docs/UpdateDocResponse";
import { UpsertWidgetsRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpsertWidgetsRequest';

@Injectable({
  providedIn: 'root'
})
export class CovidAssessmentsService extends BusinessProjectManagementService {
  private readonly endpointV1: string;
  private readonly endpointV2: string;
  private readonly docsEndpoint: string;
  private readonly docsV2Endpoint: string;

  constructor(
    @Inject(ApiClientConfig) private config,
    public httpClient: HttpClient) {
    super(httpClient);
    this.endpointV1 = this.config.endPoint + '/api/org/covid-assessments/';
    this.endpointV2 = this.config.endPoint + '/api/org/v2/covid-assessments/';
    this.docsEndpoint = this.config.endPoint + '/api/org/docs/covid-assessments/';
    this.docsV2Endpoint = this.config.endPoint + '/api/org/v2/docs/covid-assessments/';

  }

  getEndPointV1(): string {
    return this.endpointV1;
  }

  getEndPointV2(): string {
    return this.endpointV2;
  }

  // ----------------------------------- API DOC V2 ----------------------------------- //
  getActivePermission(docId: number): Observable<GetCovidAsPermissionResponse> {
    const url = this.docsV2Endpoint + `${docId}/permissions/`;
    return this.httpClient.get<GetCovidAsPermissionResponse>(url);
  }

  patchActivePermission(docId: number, request: UpdateCovidAsPermissionRequest): Observable<void> {
    const url = this.docsV2Endpoint + `${docId}/permissions/`;
    return this.httpClient.patch<void>(url, request);
  }


  // ----------------------------------- API V2 ----------------------------------- //

  getTreatmentTemplateChapterId(id: number, chapterId: number): Observable<GetChapterResponse> {
    return this.httpClient.get<GetChapterResponse>(this.endpointV2 + `${id}/latest/template/chapters/${chapterId}/`);
  }

  getDocumentTemplateChapters(id: number): Observable<ListTemplateChapterResponse> {
    return this.httpClient.get<ListTemplateChapterResponse>(this.endpointV2 + `${id}/latest/template/chapters/`);
  }

  findAllFiltered(filter?: string, filterByAnyPermission?: boolean,
                  columns?: string[]): Observable<Array<DocumentItem>> {
    let url = this.endpointV2 + `latest/`;
    url += (filter ? ('?' + filter) : '');
    url += filter ? '&sort=docId,DESC' : '?sort=docId,DESC';
    url += (filterByAnyPermission ? '&filterByAnyPermission=true' : '&filterByAnyPermission=false');
    if (columns && columns.length > 0) {
      url += '&columns='+columns.join('%7C');
    }
    return this.httpClient.get<ListDocumentResponse>(url).pipe(
      map(response => {
        return response.documents; //.sort((a: { docId: number }, b: { docId: number }) => a.docId > b.docId ? 1 : -1);
      }));
  }


  // ----------------------------------- API V1 ----------------------------------- //
  getDocColumn(): Observable<ColumnWithLocalization[]> {
    const url = this.docsEndpoint + `columns/`;
    return this.httpClient.get<ColumnWithLocalization[]>(url);
  }

  create(body: CovidAssessmentDocCreateRequest): Observable<CreateDocResponse> {
    const url = this.docsEndpoint
    return this.httpClient.post<CreateDocResponse>(url, body);
  }

  delete(id: number): Observable<void> {
    const url = this.docsEndpoint + `${id}/`;
    return this.httpClient.delete<void>(url);
  }


  update(id: number, body: CovidAssessmentDocUpdateRequest): Observable<UpdateDocResponse> {
    const url = this.docsEndpoint + `${id}/`;
    return this.httpClient.put<UpdateDocResponse>(url, body);
  }

  rename(id: number, request: DocRenameRequest): Observable<void> {
    const url = this.docsEndpoint + `${id}/rename`;
    return this.httpClient.patch<void>(url, request);
  }

  getToOverride(): Observable<CovidAssessmentOverrideOnDuplicate> {
    const url = this.endpointV1 + `override-on-duplicate`;
    return this.httpClient.get<CovidAssessmentOverrideOnDuplicate>(url);
  }

  duplicate(id: number, request: CovidAssessmentDocCreateRequest): Observable<DocumentDuplicateResponse> {
    const url = this.endpointV1 + `${id}/latest/duplicate`;
    return this.httpClient.post<DocumentDuplicateResponse>(url, request);
  }

  reopen(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/re-open`;
    return this.httpClient.post<void>(url, {});
  }

  getDetails(id: number): Observable<CovidAssessmentDetails> {
    const url = this.endpointV1 + `${id}/latest/details`;
    return this.httpClient.get<CovidAssessmentDetails>(url);
  }

  getEditorDetails(id: number): Observable<CommonDocumentEditorDetails> {
    return this.getDetails(id).pipe(map(m => {
      const response: CommonDocumentEditorDetails = {
        docId: m.docId,
        status: m.status as DocResourceStatus,
        statusLabel: m.statusLabel,
        name: m.name,
        userCheck: m.userCheck,
        description: m.description,
        warnings: [],
        internalId: m.internalId,
        parentId: m.parentId,
        parentName: m.parentName,
        completed: m.completed,
        creatorUserId: m.creatorUserId,
        updatedAt: m.updatedAt,
      }
      return response;
    }));

    // const url = this.endpointV2 + `${id}/latest/editor-details/`;
    // return this.httpClient.get<GetTreatmentForEditorDetailResponse>(url);
  }

  upsertWidgets(id: number, widgets: UpsertWidgetsRequest): Observable<void> {
    const url = this.endpointV2 + `${id}/latest/widgets/`;
    return this.httpClient.post<void>(url, widgets);
  }

  releaseLock(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/lock`;
    return this.httpClient.delete<void>(url, {});
  }

  lockResource(id: number, token: number): Observable<DocumentLockResponse> {
    let url = this.endpointV1 + `${id}/latest/lock`;
    if (token !== null) {
      url = `${url}?token=${token}`;
    }
    return this.httpClient.post<DocumentLockResponse>(url, {});
  }

  publish(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/publish`;
    return this.httpClient.post<void>(url, {});
  }

  refreshResults(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/refresh-results`;
    return this.httpClient.post<void>(url, {});
  }

  deleteMultiple(ids: number[]): Observable<{ failed: number[] }> {
    const url = this.docsEndpoint + `by-ids`;
    return this.httpClient.post<{ failed: number[] }>(url, {ids});
  }

  userChecked(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/user-checked`;
    return this.httpClient.post<void>(url, {});
  }

  preview(id: number) {
    const url = this.downloadPreviewPdfUrl(id);
    return this.httpClient.get(url, {responseType: 'text'});
  }


  getDocUserPermission(docId: number): Observable<GetCovidAsAvailableOperationsResponse> {
    const url = this.endpointV2 + `${docId}/latest/user-operations/`;
    return this.httpClient.get<GetCovidAsAvailableOperationsResponse>(url);
  }
}

